/**
 * Блок движений платёжных систем с сопоставлением клиентов.
 *
 * Flow: фильтры → /api/treasury/psp-transactions → таблица движений →
 *       несопоставленное движение ведёт к привязке клиента →
 *       PUT /api/treasury/psp-customers/{source}/{ref}/client →
 *       перезагрузка страницы движений.
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadPspTransactions, bindPspCustomer.
 *
 * Связанные файлы:
 *   - src/App.jsx — размещает блок на экране;
 *   - docs/specs/BL-2026-09-07-0a54-psp-on-screen.md — техспека.
 */

import { useCallback, useEffect, useState } from 'react'

import { bindPspCustomer, loadPspTransactions } from './api'

const SOURCE_TITLES = {
  praxis: 'Praxis Cashier',
  b2binpay: 'B2BinPay',
  coinsbuy: 'Coinsbuy',
}

const DIRECTION_TITLES = {
  deposit: 'ввод',
  withdrawal: 'вывод',
}

const STATUS_TITLES = {
  pending: 'в обработке',
  completed: 'проведено',
  failed: 'отклонено',
  cancelled: 'отменено',
}

/** Строк на страницу. Совпадает с умолчанием сервера. */
const PAGE = 100

export function formatAmount(value) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return value
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 8 })
}

/** Показывает момент в UTC — время системы едино (LR-005). */
export function formatMoment(iso) {
  if (!iso) return '—'
  return new Date(iso).toISOString().replace('T', ' ').slice(0, 16) + ' UTC'
}

/**
 * Клетка клиента: показывает сопоставленного либо предлагает привязать.
 *
 * Несопоставленный клиент — рабочее состояние, а не ошибка (BR-023),
 * поэтому строка не прячется и не красится тревожно; она лишь предлагает
 * действие тому, у кого есть право.
 */
function ClientCell({ row, canBind, onBind }) {
  const [editing, setEditing] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  if (row.client_code) {
    return (
      <span title={row.client_name || ''}>
        {row.client_name || row.client_code}
        {canBind && (
          <button
            type="button"
            className="link"
            onClick={() => onBind(row.source_code, row.client_ref, '')}
            title="снять привязку"
          >
            {' '}
            ✕
          </button>
        )}
      </span>
    )
  }

  if (!row.client_ref) {
    // Провайдер не назвал плательщика вовсе — привязывать нечего.
    return <span className="muted">не назван</span>
  }

  if (!canBind) {
    return <span className="muted">{row.client_ref} — не сопоставлен</span>
  }

  if (!editing) {
    return (
      <button type="button" className="link" onClick={() => setEditing(true)}>
        {row.client_ref} — сопоставить
      </button>
    )
  }

  async function submit(event) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await onBind(row.source_code, row.client_ref, code)
      setEditing(false)
      setCode('')
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="inline">
      <input
        value={code}
        placeholder="код клиента"
        onChange={(e) => setCode(e.target.value)}
        aria-label={`код клиента для ${row.client_ref}`}
      />
      <button type="submit" disabled={busy || !code}>
        {busy ? '…' : 'Связать'}
      </button>
      <button type="button" onClick={() => setEditing(false)}>
        Отмена
      </button>
      {error && <span className="error">{error}</span>}
    </form>
  )
}

export default function PspTransactionsPanel({ user }) {
  const [report, setReport] = useState(null)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({
    source: '',
    direction: '',
    status: '',
    matched: '',
  })
  const [offset, setOffset] = useState(0)

  // Право записи — то же, что у ручки привязки: она меняет принадлежность
  // денег. Наблюдатель видит движения, но не связывает их.
  const canBind = user?.role === 'admin' || user?.role === 'backoffice' || user?.role === 'treasurer'

  const load = useCallback(() => {
    loadPspTransactions({ ...filters, limit: PAGE, offset })
      .then((data) => {
        setReport(data)
        setError('')
      })
      .catch((e) => setError(e.message))
  }, [filters, offset])

  useEffect(() => {
    load()
  }, [load])

  const bind = useCallback(
    async (source, ref, code) => {
      await bindPspCustomer(source, ref, code)
      // Перезагрузка, а не правка строки на месте: привязка достаёт ВСЕ
      // движения плательщика, включая те, что на этой странице, и
      // подрисовать одну строку значило бы показать неправду о соседних.
      load()
    },
    [load],
  )

  if (error) {
    return (
      <section className="card">
        <h2>Движения платёжных систем</h2>
        <div className="error">{error}</div>
      </section>
    )
  }

  if (!report) {
    return (
      <section className="card">
        <h2>Движения платёжных систем</h2>
        <p className="muted">Загрузка…</p>
      </section>
    )
  }

  if (!report.sources || report.sources.length === 0) {
    return null
  }

  function setFilter(name, value) {
    setOffset(0)
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  const rows = report.transactions || []
  const shownTo = report.offset + rows.length

  return (
    <section className="card">
      <h2>Движения платёжных систем</h2>

      <div className="filters">
        <select value={filters.source} onChange={(e) => setFilter('source', e.target.value)} aria-label="платёжная система">
          <option value="">все системы</option>
          {report.sources.map((s) => (
            <option key={s} value={s}>
              {SOURCE_TITLES[s] || s}
            </option>
          ))}
        </select>
        <select value={filters.direction} onChange={(e) => setFilter('direction', e.target.value)} aria-label="направление">
          <option value="">ввод и вывод</option>
          <option value="deposit">только ввод</option>
          <option value="withdrawal">только вывод</option>
        </select>
        <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)} aria-label="статус">
          <option value="">любой статус</option>
          {Object.entries(STATUS_TITLES).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select value={filters.matched} onChange={(e) => setFilter('matched', e.target.value)} aria-label="сопоставление">
          <option value="">все клиенты</option>
          <option value="false">без клиента</option>
          <option value="true">с клиентом</option>
        </select>
      </div>

      {report.unmatched > 0 && (
        <p className="warn">
          Клиентов платёжных систем без сопоставления: {report.unmatched}. Пока клиент
          не назван, движение видно, но в клиентские средства не входит.
        </p>
      )}

      {report.totals && report.totals.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Система</th>
              <th>Валюта</th>
              <th className="num">Проведено</th>
              <th className="num">Движений</th>
            </tr>
          </thead>
          <tbody>
            {report.totals.map((t) => (
              <tr key={`${t.source_code}-${t.currency_code}`}>
                <td>{SOURCE_TITLES[t.source_code] || t.source_code}</td>
                <td>{t.currency_code}</td>
                <td className="num">{formatAmount(t.total)}</td>
                <td className="num">{t.wallets}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rows.length === 0 ? (
        <p className="muted">Движений по этому отбору нет.</p>
      ) : (
        // Страница движений прокручивается ВНУТРИ блока. Сто строк дают
        // колонку почти в восемь тысяч пикселей, и рядом с ней остатки
        // и оценка стояли бы блоками по шестьсот — то есть не рядом,
        // а над семью тысячами пустоты. Счётчик и перелистывание при
        // этом остаются под таблицей, на виду.
        <div className="scroll-rows">
        <table>
          <thead>
            <tr>
              <th>Момент</th>
              <th>Система</th>
              <th>Направление</th>
              <th className="num">Сумма</th>
              <th>Валюта</th>
              <th>Статус</th>
              <th>Клиент</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{formatMoment(row.occurred_at)}</td>
                <td>{SOURCE_TITLES[row.source_code] || row.source_code}</td>
                <td>{DIRECTION_TITLES[row.direction] || row.direction}</td>
                <td className="num">{formatAmount(row.amount)}</td>
                <td>{row.currency_code}</td>
                <td title={`статус источника: ${row.status_source}`}>
                  {STATUS_TITLES[row.status] || row.status}
                </td>
                <td>
                  <ClientCell row={row} canBind={canBind} onBind={bind} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      <p className="muted">
        Показано {rows.length === 0 ? 0 : report.offset + 1}–{shownTo} из {report.total}.{' '}
        <button type="button" disabled={report.offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
          Назад
        </button>{' '}
        <button type="button" disabled={shownTo >= report.total} onClick={() => setOffset(offset + PAGE)}>
          Вперёд
        </button>
      </p>
    </section>
  )
}
